import type React from "react";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { List, IconButton, Collapse, Box, useTheme } from "@mui/material";
import {
  KeyboardArrowDown as ArrowDownIcon,
  Sync as LogislyIcon,
  BarChart as StatisticsIcon,
  Person as DriverIcon,
  LocalShipping as TruckIcon,
  LocationOn as LocationIcon,
  LocationCity as LocationGroupIcon,
  AssignmentTurnedIn as ConfigIcon,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import SidebarMenuItem from "../SidebarMenuItem";
import { rawColor } from "../../constants/colors";
import { TestId } from "../../constants/testIds";

type TranslationKeys = keyof typeof translations.en;

interface LogislySpxOnCallMenuProps {
  onNavigate?: () => void;
}

const LogislySpxOnCallMenu: React.FC<LogislySpxOnCallMenuProps> = ({ onNavigate }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const manuallyCollapsedRef = useRef(false);
  const manuallyExpandedRef = useRef(false);

  // Check if current path is a logisly spx oncall page
  const isLogislySection = useMemo(() => {
    return location.pathname.startsWith("/logisly-spx-oncall");
  }, [location.pathname]);

  const isLogislySpxOnCallPage = useMemo(() => {
    return location.pathname === "/logisly-spx-oncall";
  }, [location.pathname]);

  const isStatisticsPage = useMemo(() => {
    return location.pathname.startsWith("/logisly-spx-oncall/statistics");
  }, [location.pathname]);

  const isDriversPage = useMemo(() => {
    return location.pathname.startsWith("/logisly-spx-oncall/drivers");
  }, [location.pathname]);

  const isTrucksPage = useMemo(() => {
    return location.pathname.startsWith("/logisly-spx-oncall/trucks");
  }, [location.pathname]);

  const isLocationsPage = useMemo(() => {
    return location.pathname.startsWith("/logisly-spx-oncall/locations");
  }, [location.pathname]);

  const isLocationGroupsPage = useMemo(() => {
    return location.pathname.startsWith("/logisly-spx-oncall/location-groups");
  }, [location.pathname]);

  const isConfigsV2Page = useMemo(() => {
    return location.pathname.startsWith("/logisly-spx-oncall/order-taker-configs-v2");
  }, [location.pathname]);

  // Initialize expanded state: collapsed by default, expanded if on logisly spx oncall page
  const [logislySpxOnCallExpanded, setLogislySpxOnCallExpanded] = useState(() => isLogislySection);

  // Auto-expand/collapse based on logisly spx oncall page status and manual state
  useEffect(() => {
    if (isLogislySection) {
      // On logisly spx oncall page: respect manual state
      if (manuallyExpandedRef.current) {
        // Keep expanded if manually expanded
        setLogislySpxOnCallExpanded(true);
      } else if (manuallyCollapsedRef.current) {
        // Keep collapsed if manually collapsed
        setLogislySpxOnCallExpanded(false);
      } else {
        // Auto-expand on logisly spx oncall pages if no manual state
        setLogislySpxOnCallExpanded(true);
      }
    } else {
      // Not on logisly spx oncall page: always auto-collapse and reset manual state
      setLogislySpxOnCallExpanded(false);
      manuallyExpandedRef.current = false;
      manuallyCollapsedRef.current = false;
    }
  }, [isLogislySection]);

  const toggleLogislySpxOnCall = useCallback(() => {
    setLogislySpxOnCallExpanded((prev) => {
      const newValue = !prev;
      // Track manual state
      if (newValue) {
        // Manually expanded
        manuallyExpandedRef.current = true;
        manuallyCollapsedRef.current = false;
      } else {
        // Manually collapsed
        manuallyCollapsedRef.current = true;
        manuallyExpandedRef.current = false;
      }
      return newValue;
    });
  }, []);

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          position: "relative",
        }}
      >
        <SidebarMenuItem
          to="/logisly-spx-oncall"
          selected={isLogislySection}
          icon={<LogislyIcon />}
          text="Logisly"
          sx={{ flex: 1 }}
          onNavigate={onNavigate}
          data-testid={TestId.SIDEBAR.MENU_ITEM_LOGISLY_SPX_ONCALL}
        />
        <IconButton
          size="small"
          onClick={toggleLogislySpxOnCall}
          sx={{
            transform: logislySpxOnCallExpanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: theme.transitions.create("transform", {
              duration: theme.transitions.duration.shorter,
            }),
            position: "absolute",
            right: 4,
            zIndex: 1,
            color: rawColor.white.transparent["07"],
            "&:hover": {
              backgroundColor: rawColor.white.transparent["12"],
            },
          }}
        >
          <ArrowDownIcon />
        </IconButton>
      </Box>
      <Collapse in={logislySpxOnCallExpanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <SidebarMenuItem
            to="/logisly-spx-oncall"
            selected={isLogislySpxOnCallPage}
            text="Logisly SPX OnCall Orders"
            isSubmenu
            textProps={{ fontSize: "0.875rem" }}
            sx={{ pl: 4, py: 0.375, minHeight: "unset" }}
            onNavigate={onNavigate}
          />
          <SidebarMenuItem
            to="/logisly-spx-oncall/statistics"
            selected={isStatisticsPage}
            icon={<StatisticsIcon fontSize="small" />}
            text="Logisly SPX OnCall Order Statistics"
            isSubmenu
            textProps={{ fontSize: "0.875rem" }}
            sx={{ pl: 4, py: 0.375, minHeight: "unset" }}
            onNavigate={onNavigate}
          />
          <SidebarMenuItem
            to="/logisly-spx-oncall/drivers"
            selected={isDriversPage}
            icon={<DriverIcon fontSize="small" />}
            text="Drivers"
            isSubmenu
            textProps={{ fontSize: "0.875rem" }}
            sx={{ pl: 4, py: 0.375, minHeight: "unset" }}
            onNavigate={onNavigate}
          />
          <SidebarMenuItem
            to="/logisly-spx-oncall/trucks"
            selected={isTrucksPage}
            icon={<TruckIcon fontSize="small" />}
            text="Trucks"
            isSubmenu
            textProps={{ fontSize: "0.875rem" }}
            sx={{ pl: 4, py: 0.375, minHeight: "unset" }}
            onNavigate={onNavigate}
          />
          <SidebarMenuItem
            to="/logisly-spx-oncall/locations"
            selected={isLocationsPage}
            icon={<LocationIcon fontSize="small" />}
            text="Locations"
            isSubmenu
            textProps={{ fontSize: "0.875rem" }}
            sx={{ pl: 4, py: 0.375, minHeight: "unset" }}
            onNavigate={onNavigate}
          />
          <SidebarMenuItem
            to="/logisly-spx-oncall/location-groups"
            selected={isLocationGroupsPage}
            icon={<LocationGroupIcon fontSize="small" />}
            text="Location Groups"
            isSubmenu
            textProps={{ fontSize: "0.875rem" }}
            sx={{ pl: 4, py: 0.375, minHeight: "unset" }}
            onNavigate={onNavigate}
          />
          <SidebarMenuItem
            to="/logisly-spx-oncall/order-taker-configs-v2"
            selected={isConfigsV2Page}
            icon={<ConfigIcon fontSize="small" />}
            text="Order Taker Configs V2"
            isSubmenu
            textProps={{ fontSize: "0.875rem" }}
            sx={{ pl: 4, py: 0.375, minHeight: "unset" }}
            onNavigate={onNavigate}
          />
        </List>
      </Collapse>
    </Box>
  );
};

export default LogislySpxOnCallMenu;
